import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import APIClient from "@/APIClient";
import { QueryKey } from "./query-keys";
import { ref, watch, type Ref } from "vue";
import type {
  DRDrawing,
  PaginationParams,
  UpdateDrawingPayload,
} from "@/types";
import { drawingRepository } from "@/repositories/drawing.repository";
import { useToast } from "@/composable/useToast";
import { localDrawingRepository } from "@/repositories/local-drawing.repository";
import { useDrawingStore } from "../fileStore";

export const useGetArchivedDrawings = (
  pagination: Ref<PaginationParams>,
  filters: Ref<{ search?: string; projectId?: number }>,
) =>
  useQuery({
    queryKey: [QueryKey.DRAWINGS, "archived", pagination, filters],
    queryFn: async () => {
      const filterParams: any = {};

      if (filters.value.search?.trim()?.length) {
        filterParams.search = filters.value.search;
      }
      if (filters.value.projectId) {
        filterParams.projectId = filters.value.projectId;
      }

      return await APIClient.fetchDrawings({
        ...pagination.value,
        ...filterParams,
        archived: true,
      });
    },
  });

export const useGetDrawingsByProject = (
  projectId: Ref<number>,
  pagination: Ref<PaginationParams>,
  search: Ref<string>,
) => {
  const localDrawings = ref<DRDrawing[]>([]);

  const getLocalDrawings = async (pid: number) => {
    localDrawings.value = await drawingRepository.getMany({
      projectId: pid,
    });
  };

  watch(
    () => projectId.value,
    (newProjectId) => {
      if (newProjectId) {
        getLocalDrawings(newProjectId);
      }
    },
    { immediate: true },
  );

  const query = useQuery({
    queryKey: [QueryKey.DRAWINGS, "mine", projectId, pagination, search],
    queryFn: async (): Promise<{
      drawings?: DRDrawing[];
    }> => {
      const filters = search.value?.trim()?.length
        ? { search: search.value.trim() }
        : {};
      return await APIClient.fetchDrawings({
        ...pagination.value,
        ...filters,
        projectId: projectId.value,
        archived: false,
      }).then(async (drawings) => {
        await drawingRepository.createManyFromServer(drawings);
        getLocalDrawings(projectId.value);
        return {
          drawings,
        };
      });
    },
    retry: 1,
  });

  return {
    ...query,
    localDrawings,
  };
};

export const useGetLastDrawings = (ids: Ref<number[]>) => {
  return useQuery({
    queryKey: [QueryKey.DRAWINGS, "last", ids],
    queryFn: async (): Promise<{
      drawings?: DRDrawing[];
    }> => {
      const serverIds = ids.value.filter((id) => id > 0);
      if (serverIds.length === 0) {
        return { drawings: [] };
      }
      return await APIClient.fetchDrawings({
        take: 50,
        skip: 0,
        sortBy: "createdAt",
        order: "desc",
        archived: false,
        ids: serverIds,
      })
        .then(async (drawings) => {
          await drawingRepository.createManyFromServer(drawings);
          const res = await drawingRepository.getManyByIds(ids.value);
          return {
            drawings: res,
          };
        })
        .catch(async () => {
          const res = await drawingRepository.getManyByIds(ids.value);
          return {
            drawings: res,
          };
        });
    },
    retry: 1,
  });
};

/**
 * Plans rattachés à un ouvrage (page Ouvrage). Le cache local sert de repli
 * hors ligne : les plans déjà synchronisés restent consultables.
 */
export const useGetDiagnosticDrawings = (
  diagnosticId: Ref<number | undefined>,
) => {
  return useQuery({
    queryKey: [QueryKey.DRAWINGS, "diagnostic", diagnosticId],
    queryFn: async (): Promise<DRDrawing[]> => {
      const id = diagnosticId.value;
      if (!id) return [];
      try {
        const drawings = await APIClient.fetchDrawings({
          take: 50,
          skip: 0,
          sortBy: "createdAt",
          order: "desc",
          archived: false,
          diagnosticId: id,
        });
        await drawingRepository.createManyFromServer(drawings);
        return drawings;
      } catch {
        // Hors ligne : on montre les plans déjà connus localement plutôt qu'une
        // liste vide, qui laisserait croire que le rattachement a été perdu.
        return await drawingRepository.getManyByDiagnostic(id);
      }
    },
    enabled: () => !!diagnosticId.value,
    retry: 1,
  });
};

export const useGetOtherMembersDrawings = (
  projectId: Ref<number>,
  userId: Ref<number | undefined>,
  pagination: Ref<PaginationParams>,
) =>
  useQuery({
    queryKey: [QueryKey.DRAWINGS, "others", projectId, pagination, userId],
    queryFn: async (): Promise<DRDrawing[]> => {
      if (!userId.value) throw new Error("User ID is required");
      return await APIClient.fetchDrawings({
        ...pagination.value,
        userId: userId.value,
        projectId: projectId.value,
      });
    },
    enabled: !!userId.value,
  });

export const useArchiveDrawing = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { syncDrawings } = useDrawingStore();

  return useMutation({
    mutationFn: async (drawingId: number) => {
      return await APIClient.archiveDrawing(drawingId);
    },
    onSuccess: async (_, drawingId) => {
      await localDrawingRepository.deleteById(drawingId);
      await drawingRepository.deleteById(drawingId);
      queryClient.invalidateQueries({ queryKey: [QueryKey.DRAWINGS] });
      syncDrawings();
      showToast("drawing.archive.success");
    },
    onError: () => {
      showToast("drawing.archive.error", "warning");
    },
  });
};

export const useUnarchiveDrawing = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: async (drawing: DRDrawing) => {
      return await APIClient.unArchiveDrawing(drawing.id);
    },
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: [QueryKey.DRAWINGS] });
      showToast("drawing.unarchive.success");
    },
    onError: () => {
      showToast("drawing.unarchive.error", "warning");
    },
  });
};

export const useDeleteDrawing = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: async (drawing: DRDrawing) => {
      return await APIClient.deleteDrawing(drawing.id);
    },
    onSuccess: async (_, drawing) => {
      await localDrawingRepository.deleteById(drawing.id);
      await drawingRepository.deleteById(drawing.id);
      queryClient.invalidateQueries({ queryKey: [QueryKey.DRAWINGS] });
      showToast("drawing.delete.success");
    },
    onError: () => {
      showToast("drawing.delete.error", "warning");
    },
  });
};

export const useArchiveManyDrawings = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { syncDrawings } = useDrawingStore();

  return useMutation({
    mutationFn: async (drawingIds: number[]) => {
      return await Promise.all(
        drawingIds.map((id) => APIClient.archiveDrawing(id)),
      );
    },
    onSuccess: async (_, drawingIds) => {
      await localDrawingRepository.deleteManyById(drawingIds);
      await drawingRepository.deleteManyById(drawingIds);
      queryClient.invalidateQueries({ queryKey: [QueryKey.DRAWINGS] });
      syncDrawings();
      showToast("drawing.archive-many.success");
    },
    onError: () => {
      showToast("drawing.archive-many.error", "warning");
    },
  });
};

export const useUnarchiveManyDrawings = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: async (drawingIds: number[]) => {
      return await Promise.all(
        drawingIds.map((id) => APIClient.unArchiveDrawing(id)),
      );
    },
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: [QueryKey.DRAWINGS] });
      showToast("drawing.unarchive-many.success");
    },
    onError: () => {
      showToast("drawing.unarchive-many.error", "warning");
    },
  });
};

export const useDeleteManyDrawings = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: async (drawingIds: number[]) => {
      return await Promise.all(
        drawingIds.map((id) => APIClient.deleteDrawing(id)),
      );
    },
    onSuccess: async (_, drawingIds) => {
      await localDrawingRepository.deleteManyById(drawingIds);
      await drawingRepository.deleteManyById(drawingIds);
      queryClient.invalidateQueries({ queryKey: [QueryKey.DRAWINGS] });
      showToast("drawing.delete-many.success");
    },
    onError: () => {
      showToast("drawing.delete-many.error", "warning");
    },
  });
};

export const useMoveManyDrawings = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: async ({
      projectId,
      ids,
    }: {
      projectId: number;
      ids: number[];
    }) => {
      return await Promise.all(
        ids.map((id) => APIClient.updateDrawing(id, { projectId })),
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKey.DRAWINGS] });
      showToast("drawing.move-many.success");
    },
    onError: () => {
      showToast("drawing.move-many.error", "warning");
    },
  });
};

export const useUpdateDrawing = (id: Ref<number | undefined>) => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { syncDrawings } = useDrawingStore();

  return useMutation({
    mutationFn: async (payload: UpdateDrawingPayload) => {
      if (!id.value) throw new Error("Drawing ID is required");
      return await APIClient.updateDrawing(id.value, payload);
    },
    onSuccess: async (data: any) => {
      queryClient.invalidateQueries({ queryKey: [QueryKey.DRAWINGS] });
      syncDrawings();
      showToast("drawing.update.success");

      if (!id.value) return;

      await drawingRepository.update(id.value, {
        name: data.fileName,
        projectId: data.project_id,
        diagnostic: data.diagnostic ?? null,
      });
    },
    onError: () => {
      showToast("drawing.update.error", "warning");
    },
  });
};

// remove archived and not existing drawings from local
// ! IMPORTANT : this query must be enabled only when all drawings are synced
export const useSyncLocalFiles = () => {
  const queryClient = useQueryClient();
  const drawingStore = useDrawingStore();

  return useQuery({
    queryKey: ["sync-archives"],
    queryFn: async (): Promise<number[]> => {
      if (!drawingStore.localDrawings || !drawingStore.areAllSynced)
        throw new Error("Drawings are not all synced yet");

      const fileIds = await drawingRepository.getAllIds();
      const fileIdsToFetch = fileIds.filter((id) => id > 0); // filter out negative IDs

      if (fileIdsToFetch.length > 0) {
        const serverDrawings = await APIClient.fetchDrawings({
          take: 500,
          skip: 0,
          sortBy: "createdAt",
          order: "asc",
          archived: true,
          ids: fileIdsToFetch,
        });

        const idsToRemove = serverDrawings.map((drawing) => drawing.id);

        if (idsToRemove.length > 0) {
          await Promise.all([
            drawingRepository.deleteManyById(idsToRemove),
            localDrawingRepository.deleteManyById(idsToRemove),
          ]);
          queryClient.invalidateQueries({ queryKey: [QueryKey.DRAWINGS] });
        }

        return idsToRemove;
      }

      return [];
    },
    enabled: () => !!drawingStore.localDrawings && !!drawingStore.areAllSynced,
    refetchInterval: 1000 * 60 * 60 * 24,
  });
};

export const useGetDrawingImages = (drawingId: Ref<number | undefined>) => {
  return useQuery({
    queryKey: [QueryKey.DRAWINGS_IMAGES, drawingId],
    queryFn: async () => {
      if (!drawingId.value) throw new Error("Drawing ID is required");
      return await APIClient.fetchDrawingImages({ drawingId: drawingId.value });
    },
    enabled: () => !!drawingId.value,
  });
};

export const useGetDrawingBranches = (drawingId: Ref<number | undefined>) => {
  return useQuery({
    queryKey: [QueryKey.DRAWING_BRANCHES, drawingId],
    queryFn: async () => {
      if (!drawingId.value) throw new Error("Drawing ID is required");
      return await APIClient.fetchDrawingBranches(drawingId.value);
    },
    enabled: () => !!drawingId.value,
  });
};

export const useSwitchDrawingBranch = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { syncDrawings } = useDrawingStore();

  return useMutation({
    mutationFn: async ({
      drawingId,
      branchId,
    }: {
      drawingId: number;
      branchId: number;
    }) => {
      return await APIClient.switchDrawingBranch(drawingId, branchId);
    },
    onSuccess: (_, { drawingId }) => {
      queryClient.invalidateQueries({ queryKey: [QueryKey.DRAWINGS] });
      queryClient.invalidateQueries({
        queryKey: [QueryKey.DRAWING_BRANCHES, drawingId],
      });
      syncDrawings();
      showToast("branch.switch.success");
    },
    onError: () => {
      showToast("branch.switch.error", "warning");
    },
  });
};

export const useRenameBranch = () => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: async ({
      drawingId,
      branchId,
      name,
    }: {
      drawingId: number;
      branchId: number;
      name: string;
    }) => {
      return await APIClient.renameBranch(drawingId, branchId, name);
    },
    onSuccess: (_, { drawingId }) => {
      queryClient.invalidateQueries({
        queryKey: [QueryKey.DRAWING_BRANCHES, drawingId],
      });
      showToast("branch.rename.success");
    },
    onError: () => {
      showToast("branch.rename.error", "warning");
    },
  });
};
